import {
  computed,
  inject,
  InjectionKey,
  onScopeDispose,
  provide,
  Ref,
} from 'vue';

/**
 * スクロールを担う要素。
 * 旧レイアウトはページ全体（window）がスクロールするが、
 * 新レイアウトではgridのcontent領域がスクロールコンテナになる。
 */
type ScrollContainerRef = Readonly<Ref<HTMLElement | null | undefined>>;

const scrollContainerKey: InjectionKey<ScrollContainerRef> = Symbol(
  'layoutScrollContainer',
);

/**
 * コンポーネントツリーの外（ルーターのscrollBehavior）から参照するための、
 * 現在マウントされているshellのスクロールコンテナ。
 */
let activeScrollContainer: ScrollContainerRef | undefined;

/** 新レイアウトのshellから、スクロールするcontent要素を渡す */
export const provideLayoutScrollContainer = (el: ScrollContainerRef): void => {
  provide(scrollContainerKey, el);
  activeScrollContainer = el;
  onScopeDispose(() => {
    if (activeScrollContainer === el) activeScrollContainer = undefined;
  });
};

/** 旧レイアウト（windowがスクロール）の場合はundefinedを返す */
export const getLayoutScrollElement = (): HTMLElement | undefined =>
  activeScrollContainer?.value ?? undefined;

export const useLayoutScrollContainer = () => {
  const injected = inject(scrollContainerKey, undefined);

  /** provideされていない場合（旧レイアウト・Storybook単体表示）はwindow */
  const scrollContainer = computed<HTMLElement | Window>(
    () => injected?.value ?? window,
  );

  const getScrollY = (): number => {
    const c = scrollContainer.value;
    return c instanceof Window ? c.scrollY : c.scrollTop;
  };

  const scrollTo = (options: ScrollToOptions): void => {
    scrollContainer.value.scrollTo(options);
  };

  const scrollToTop = (behavior: ScrollBehavior = 'auto'): void => {
    scrollTo({ top: 0, behavior });
  };

  return {
    scrollContainer,
    getScrollY,
    scrollTo,
    scrollToTop,
  };
};
